import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent } from "@/ui/atoms";
import { FileUploadForm } from "@/ui/organisms";
import { routerPaths } from "@/ui/router/routerPaths";
import { ShieldX } from "lucide-react";
import { EMPTY_ARRAY } from "@/utils/utils";

type TDealOption = {
    id: string;
    name: string;
    code: string;
};

/**
 * View parameters (injected dependencies)
 */
export type TViewUploadFinJournalsParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useDeals: () => { data: TDealOption[] | null; loading: boolean };
        onUploadFile: (data: {
            dealId: string;
            period: string;
            fileType: string;
            file: File;
            contributorIds?: string[];
        }) => Promise<void>;
        useContributorCandidates: (dealId: string | null) => {
            data: { id: string; label: string }[];
            loading: boolean;
        };
        canUpload: boolean;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewUploadFinJournalsProps = Record<string, never>;

/**
 * Creates the Upload FinJournals view component
 */
export const createViewUploadFinJournals = ({ useCases }: TViewUploadFinJournalsParams) => {
    const ViewUploadFinJournals: React.FC<TViewUploadFinJournalsProps> = () => {
        const { onMount, onUnmount, useDeals, onUploadFile, useContributorCandidates, canUpload } =
            useCases();
        const navigate = useNavigate();

        const dealsState = useDeals();
        const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
        const contributorCandidates = useContributorCandidates(selectedDealId);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const handleSuccess = () => {
            // Navigate to the files list (filtered to FinJournals)
            navigate(routerPaths.files.finJournals);
        };

        if (!canUpload) {
            return (
                <PageLayout
                    title="Upload FinJournals"
                    description="Upload Financial Journals files for processing"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="mb-4 rounded-full bg-muted p-6">
                                    <ShieldX className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">Upload Not Available</h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    Financial Journals upload is only available for Business
                                    Partners, Account Managers, and Accountants with upload
                                    permissions enabled.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Upload FinJournals"
                description="Upload Financial Journals files for processing"
            >
                <Card className="max-w-2xl">
                    <CardContent className="pt-6">
                        <FileUploadForm
                            preselectedFileType="financial_journals"
                            lockedFileType={true}
                            deals={dealsState.data ?? EMPTY_ARRAY}
                            onUploadFile={onUploadFile}
                            onSuccess={handleSuccess}
                            showCancel={false}
                            contributorOptions={contributorCandidates.data}
                            contributorsLoading={contributorCandidates.loading}
                            onDealChange={(dealId) => setSelectedDealId(dealId || null)}
                        />
                    </CardContent>
                </Card>
            </PageLayout>
        );
    };

    return ViewUploadFinJournals;
};
